import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { getPayPeriodInfo } from '../utils/dateUtils';

const PayrollAnalytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState('all');
  const [selectedRank, setSelectedRank] = useState('all');
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Fetch user permissions
  const fetchUserPermissions = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/user/permissions');
      setUserPermissions(response.data);
      setPermissionsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      setError('You do not have permission to view this page.');
      setPermissionsLoaded(true);
    }
  }, []);

  // Fetch payroll analytics data
  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiClient.get('/api/admin/payroll-analytics');
      setAnalyticsData(response.data);
      
    } catch (error) {
      console.error('Failed to fetch payroll analytics:', error);
      setError('Failed to load payroll analytics. You may not have permission to view this page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchUserPermissions();
    }
  }, [user, fetchUserPermissions]);

  useEffect(() => {
    if (permissionsLoaded && userPermissions.includes('view_payroll_analytics')) {
      fetchAnalyticsData();
    } else if (permissionsLoaded) {
      setLoading(false);
      setError('You do not have permission to view payroll analytics.');
    }
  }, [permissionsLoaded, userPermissions, fetchAnalyticsData]);

  // Filter data based on selections
  const getFilteredData = () => {
    if (!analyticsData) return null;

    let filteredData = { ...analyticsData };

    if (selectedPayPeriod !== 'all') {
      const payPeriodNum = parseInt(selectedPayPeriod);
      // Filter pay period data
      if (filteredData.payPeriodTotals) {
        filteredData.payPeriodTotals = filteredData.payPeriodTotals.filter(
          period => period.payPeriod === payPeriodNum
        );
      }
      // Filter user data
      if (filteredData.userWorkFrequency) {
        filteredData.userWorkFrequency = filteredData.userWorkFrequency.map(user => ({
          ...user,
          payPeriods: user.payPeriods.filter(period => period.payPeriod === payPeriodNum)
        })).filter(user => user.payPeriods.length > 0);
      }
    }

    if (selectedRank !== 'all') {
      // Filter user data by rank
      if (filteredData.userWorkFrequency) {
        filteredData.userWorkFrequency = filteredData.userWorkFrequency.filter(
          user => user.rank === selectedRank
        );
      }
    }

    return filteredData;
  };

  const renderPayPeriodTotals = (data) => {
    if (!data.payPeriodTotals || data.payPeriodTotals.length === 0) {
      return <p>No pay period data available.</p>;
    }

    return (
      <div className="analytics-table">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderRight: '1px solid #e5e7eb' }}>
                Pay Period
              </th>
              <th style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                Captain
              </th>
              <th style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                Lieutenant
              </th>
              <th style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                Sergeant
              </th>
              <th style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                Lifeguard
              </th>
              <th style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                Rookie
              </th>
              <th style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                Stick 1 Total
              </th>
              <th style={{ padding: '12px', textAlign: 'center' }}>
                Stick 2 Total
              </th>
            </tr>
          </thead>
          <tbody>
            {data.payPeriodTotals.map((period, index) => (
              <tr key={period.payPeriod} style={{ 
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
              }}>
                <td style={{ padding: '12px', fontWeight: 'bold', borderRight: '1px solid #e5e7eb' }}>
                  Pay Period {period.payPeriod}
                </td>
                <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                  {period.rankTotals.Captain || 0}
                </td>
                <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                  {period.rankTotals.Lieutenant || 0}
                </td>
                <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                  {period.rankTotals.Sergeant || 0}
                </td>
                <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                  {period.rankTotals.Lifeguard || 0}
                </td>
                <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                  {period.rankTotals.Rookie || 0}
                </td>
                <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                  {period.stickTotals.stick1 || 0}
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  {period.stickTotals.stick2 || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderUserWorkFrequency = (data) => {
    if (!data.userWorkFrequency || data.userWorkFrequency.length === 0) {
      return <p>No user work frequency data available.</p>;
    }

    return (
      <div className="user-frequency-container">
        {data.userWorkFrequency.map((user) => (
          <div key={user.userId} className="user-card" style={{
            margin: '16px 0',
            padding: '16px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '1px solid #f3f4f6'
            }}>
              <h4 style={{ margin: 0, color: '#374151' }}>
                {user.firstName} {user.lastName}
              </h4>
              <span style={{ 
                padding: '4px 8px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {user.rank}
              </span>
            </div>

            {user.payPeriods.length === 0 ? (
              <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No work assignments found.</p>
            ) : (
              <div className="pay-periods-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '12px'
              }}>
                {user.payPeriods.map((period) => (
                  <div key={period.payPeriod} style={{
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <h5 style={{ margin: '0 0 8px 0', color: '#374151' }}>
                      Pay Period {period.payPeriod}
                    </h5>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <strong>Total Days:</strong> {period.totalDays}
                      </div>
                    </div>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '16px' }}>
                      <div>
                        <strong>Stick 1:</strong> {period.stickBreakdown.stick1} days
                      </div>
                      <div>
                        <strong>Stick 2:</strong> {period.stickBreakdown.stick2} days
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (!permissionsLoaded || loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading payroll analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Access Denied</h2>
        <p>{error}</p>
      </div>
    );
  }

  const filteredData = getFilteredData();

  return (
    <div className="payroll-analytics">
      <div className="page-header">
        <h1>Payroll Analytics</h1>
        <p>Pay period totals by rank and stick, plus individual user work frequency</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Filters</h2>
        </div>
        <div style={{ display: 'flex', gap: '16px', padding: '16px 0' }}>
          <div className="form-group" style={{ minWidth: '200px' }}>
            <label className="form-label">Pay Period</label>
            <select
              value={selectedPayPeriod}
              onChange={(e) => setSelectedPayPeriod(e.target.value)}
              className="form-select"
            >
              <option value="all">All Pay Periods</option>
              {analyticsData?.availablePayPeriods?.map((period) => (
                <option key={period} value={period}>
                  Pay Period {period}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ minWidth: '200px' }}>
            <label className="form-label">Rank</label>
            <select
              value={selectedRank}
              onChange={(e) => setSelectedRank(e.target.value)}
              className="form-select"
            >
              <option value="all">All Ranks</option>
              <option value="Captain">Captain</option>
              <option value="Lieutenant">Lieutenant</option>
              <option value="Sergeant">Sergeant</option>
              <option value="Lifeguard">Lifeguard</option>
              <option value="Rookie">Rookie</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pay Period Totals */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Pay Period Totals by Rank and Stick</h2>
        </div>
        <div style={{ overflow: 'auto' }}>
          {renderPayPeriodTotals(filteredData)}
        </div>
      </div>

      {/* User Work Frequency */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">User Work Frequency</h2>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0 0' }}>
            Individual work frequency by pay period and stick
          </p>
        </div>
        {renderUserWorkFrequency(filteredData)}
      </div>
    </div>
  );
};

export default PayrollAnalytics; 